import Analytics from '@/utils/analytics';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import SalesPortalIcon from '@assets/icons/sales-portal.svg';
import { APP_CONTAINER_ROUTES, SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Avatar, SIZES } from './avatar';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { Experiments } from '@domain/configuration/experiments';
import {
  getMicroProductIconConfig,
  PROMOTED_MICROPRODUCT_ICON_SIZE,
} from '@/screens/storefront-microproducts/micro-product-icon-config';
import { InsightsHubIcon } from './locations/insights-hub-icon';
import { MenuItemIcon } from './menu-item-icon';
import { MicroProduct } from '@domain/microproducts/loaders/use-microproducts-loader';
import { OfflineDownloadProgressBar } from './offline-download-progress-bar';
import { OfflineModeBanner } from './offline-mode-banner';
import { PermissionAction, PermissionSubject } from '@/enums';
import { QueuedOfflineOrdersButton } from './offline-orders/queued-offline-orders-button';
import { RestaurantSelectorButton } from './restaurant-selector-button';
import { SalesRepButton } from './locations/sales-rep-button';
import { StackNavigationProp } from '@react-navigation/stack';
import { StatusBar, Text, TouchableOpacity, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import {
  useAuthenticatedUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import {
  useDynamicConfigValue,
  useGetExperiment,
} from '@/hooks/use-dynamic-config-value';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMarkHubAsViewed } from '@/hooks/insights-hub/use-mark-hub-as-viewed';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useOfflineQueuedOrders } from '@/hooks/use-offline-data';
import { useSalesPortalNavigationStore } from '@/experiences/sales-portal/blocs/stores/sales-portal-navigation-store';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useStatusBarColor } from '@/hooks/use-status-bar-color';
import { useStorefrontMicroProducts } from '@/screens/storefront-microproducts/blocs/use-storefront-microproducts';

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Account>,
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.MenuInfo>
>;

const BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};
const PADDING_VERTICAL = 8;
const MAX_BUTTONS_PER_SIDE = 2;

export const HeaderRestaurantSelector: React.FC = React.memo(() => {
  const { isSmallLayout } = useLayoutContext();
  const {
    employeeCan,
    selectedRestaurantRelationship,
    user,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const { setShowSalesRepUI } = useUserSettingsContext();
  const { navigate } = useNavigation<Navigation>();
  const [styles, theme] = useStyles();
  const salesRepMode = useSalesRepMode();
  const { offlineRestaurantWithQueuedOrders } = useOfflineQueuedOrders();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const insightsHubConfig = useDynamicConfigValue('insights_hub_config');
  const salesPortalConfig = useDynamicConfigValue('sales_portal');
  const { enabled: isMobileSalesPortalEnabled } = useGetExperiment(
    Experiments.SALES_PORTAL_ORDER_ENTRY_MOBILE_V1,
  );
  const { switchToExperience } = useExperienceStore();
  const { markHubViewed } = useMarkHubAsViewed();

  const { headerActionProducts } = useStorefrontMicroProducts();

  useStatusBarColor(salesRepMode ? theme.colors.primary : theme.colors.white);

  // TODO: put employee and account related items in the nav drawer on large screens
  // https://usepepper.atlassian.net/browse/PROD-789
  if (!isSmallLayout) {
    return null;
  }

  const isSupplierEmployee = Boolean(supplierEmployeeRole);
  const isRestaurantOnHold = selectedRestaurantRelationship.onHold;
  const showSalesRepButton =
    employeeCan(PermissionAction.Read, PermissionSubject.SrtPriceEditing) &&
    selectedRestaurantRelationship.salesRepModeEnabled;
  const showSalesPortalSwitch =
    isSupplierEmployee &&
    !!salesPortalConfig?.enabled &&
    isMobileSalesPortalEnabled;

  // Calculate spacers on the left and right to center the restaurant button
  const leftButtonCount = 1 + (showSalesRepButton ? 1 : 0);
  let rightButtonCount =
    // Start with 1 for either the insights or employees button
    1 +
    (offlineRestaurantWithQueuedOrders && isOfflineModeEnabled ? 1 : 0) +
    (showSalesPortalSwitch ? 1 : 0);

  // Include microproduct header action if there is still space
  let headerProduct: MicroProduct | undefined;
  if (rightButtonCount < MAX_BUTTONS_PER_SIDE && headerActionProducts[0]) {
    headerProduct = headerActionProducts[0];
    rightButtonCount += 1;
  }

  const renderBalanceSpacers = (count: number) =>
    Array.from({ length: Math.max(0, count) }, (_, index) => (
      <View key={index} style={styles.iconButton} />
    ));

  return (
    <>
      <OfflineModeBanner />
      <View style={[styles.base, salesRepMode ? styles.baseDark : null]}>
        {salesRepMode ? (
          <StatusBar
            backgroundColor={theme.colors.primary}
            barStyle="light-content"
            translucent
          />
        ) : null}
        <View style={styles.side}>
          <TouchableOpacity
            hitSlop={BUTTON_HIT_SLOP}
            onPress={() => {
              Analytics.track(
                'restaurant_selector.account_profile_button.press',
              );
              navigate(APP_CONTAINER_ROUTES.Profile);
            }}
            style={styles.iconButton}
            testID="account-profile-button"
          >
            <Avatar name={user.name} size="small" uuid={user.uuid} />
          </TouchableOpacity>
          {showSalesRepButton ? (
            <SalesRepButton
              hitSlop={BUTTON_HIT_SLOP}
              onPress={() => {
                setShowSalesRepUI(!salesRepMode);
                Analytics.track('restaurant_selector.sales_rep_button.press');
              }}
              salesRepModeEnabled={salesRepMode}
              style={styles.iconButton}
            />
          ) : null}
          {renderBalanceSpacers(rightButtonCount - leftButtonCount)}
        </View>
        <View style={styles.center}>
          <RestaurantSelectorButton />
        </View>
        <View style={[styles.side, styles.sideRight]}>
          {renderBalanceSpacers(leftButtonCount - rightButtonCount)}
          {offlineRestaurantWithQueuedOrders && isOfflineModeEnabled ? (
            <QueuedOfflineOrdersButton />
          ) : null}
          {isSupplierEmployee && insightsHubConfig?.enabled ? (
            <TouchableOpacity
              accessibilityLabel={`${i18n.t('insights_hub')} button`}
              hitSlop={BUTTON_HIT_SLOP}
              onPress={async () => {
                // TODO(DSR-2616): open action sheet for first time users
                navigate(APP_CONTAINER_ROUTES.InsightsHub);
                Analytics.track(
                  'restaurant_selector.insights_hub_button.press',
                );
                markHubViewed();
              }}
              style={styles.iconButton}
            >
              <InsightsHubIcon
                badgeStyle={styles.customBadge}
                salesRepModeEnabled={salesRepMode}
              />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              hitSlop={BUTTON_HIT_SLOP}
              onPress={() => {
                Analytics.track('restaurant_selector.employees_button.press');
                navigate(APP_CONTAINER_ROUTES.Employees);
              }}
              style={styles.iconButton}
              testID="chat-employee-button"
            >
              <EmployeesIcon
                stroke={
                  salesRepMode ? theme.colors.white : theme.colors.primary
                }
              />
            </TouchableOpacity>
          )}
          {headerProduct ? (
            <TouchableOpacity
              accessibilityLabel={headerProduct.displayName}
              accessibilityRole="button"
              hitSlop={BUTTON_HIT_SLOP}
              onPress={() => {
                Analytics.track(
                  'restaurant_selector.microproduct_button.press',
                  {
                    microProductId: headerProduct.id,
                    placement: 'mobile_header',
                  },
                );
                navigate(APP_CONTAINER_ROUTES.StorefrontMicroProduct, {
                  productId: headerProduct.id,
                });
              }}
              style={styles.iconButton}
              testID={`storefront-microproduct-header-${headerProduct.id}`}
            >
              <MenuItemIcon
                color={salesRepMode ? theme.colors.white : theme.colors.primary}
                item={getMicroProductIconConfig(headerProduct)}
                size={PROMOTED_MICROPRODUCT_ICON_SIZE}
              />
            </TouchableOpacity>
          ) : null}
          {showSalesPortalSwitch ? (
            <TouchableOpacity
              accessibilityLabel={`${i18n.t('sales_portal')} button`}
              hitSlop={BUTTON_HIT_SLOP}
              onPress={() => {
                useSalesPortalNavigationStore
                  .getState()
                  .setLastActiveRoute(SALES_PORTAL_ROUTES.Home);
                switchToExperience(ExperienceMode.SALES_PORTAL);
                Analytics.track(
                  'restaurant_selector.sales_portal_button.press',
                );
              }}
              style={styles.iconButton}
              testID="sales-portal-switch-button"
            >
              <SalesPortalIcon
                stroke={
                  salesRepMode ? theme.colors.white : theme.colors.primary
                }
              />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      <OfflineDownloadProgressBar />
      {isRestaurantOnHold ? (
        <View style={styles.onHoldHeadingColor}>
          <Text style={[TYPOGRAPHY.pBold, styles.onHoldHeadingText]}>
            {i18n.t('account_on_hold.message')}
          </Text>
        </View>
      ) : null}
    </>
  );
});

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    flexDirection: 'row',
    justifyContent: 'space-between',
    // Set minHeight to prevent the selector from shifting when the header animates
    minHeight: SIZES.small + 2 * PADDING_VERTICAL,
    paddingHorizontal: 8,
    paddingVertical: PADDING_VERTICAL,
  },
  baseDark: {
    backgroundColor: theme.colors.primary,
  },
  center: {
    // Set flexShrink so the restaurant name truncates
    flexShrink: 1,
  },
  customBadge: {
    right: 8,
  },
  iconButton: {
    marginHorizontal: 8,
    width: SIZES.small,
  },
  onHoldHeadingColor: {
    backgroundColor: theme.colors.onHoldHeading,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  onHoldHeadingText: {
    fontSize: 12,
    lineHeight: 14,
    textAlign: 'center',
  },
  side: {
    alignItems: 'center',
    flexBasis: 'auto',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 0,
  },
  sideRight: {
    justifyContent: 'flex-end',
  },
}));
